import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Bot, Settings2, Scale, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AgentCard } from "@/components/ia/AgentCard";
import { EngineConfigForm } from "@/components/ia/EngineConfigForm";
import { IaChat } from "@/components/ia/IaChat";
import { ReglasPanel } from "@/components/ia/ReglasPanel";
import { AppShell } from "@/components/layout/AppShell";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { iaService } from "@/services/ia.service";
import type { IAAgent, IaConfig, IaEjecucion, IaMetrics } from "@/types";

type Tab = "agentes" | "config" | "reglas" | "actividad";

export const Route = createFileRoute("/ia")({
  head: () => ({ meta: [{ title: "Motor IA — LicitaPro" }] }),
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => {
    const tab = search.tab;
    return typeof tab === "string" && ["agentes", "config", "reglas", "actividad"].includes(tab)
      ? { tab: tab as Tab }
      : {};
  },
  component: Page,
});

const estadoBadge: Record<string, { label: string; cls: string }> = {
  ok: { label: "Conectado", cls: "bg-success/10 text-success" },
  error: { label: "Error de conexión", cls: "bg-destructive/10 text-destructive" },
  sin_configurar: { label: "Sin configurar", cls: "bg-warning/10 text-warning" },
};

function Page() {
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();

  const configQuery = useQuery<IaConfig>({ queryKey: ["ia", "config"], queryFn: iaService.config });
  const agentsQuery = useQuery<IAAgent[]>({ queryKey: ["ia", "agents"], queryFn: iaService.agents });
  const metricsQuery = useQuery<IaMetrics>({ queryKey: ["ia", "metrics"], queryFn: iaService.metrics });
  const ejecucionesQuery = useQuery<IaEjecucion[]>({
    queryKey: ["ia", "ejecuciones"],
    queryFn: () => iaService.ejecuciones(20),
  });

  const config = configQuery.data;
  const badge = estadoBadge[config?.estadoConexion ?? "sin_configurar"];

  /**
   * Mutación única y compartida por todas las tarjetas de agente: si cada
   * tarjeta tuviera su propia mutación, activar dos agentes seguido hacía
   * que el segundo PUT partiera de una config ya obsoleta (sin el primer
   * cambio) y lo pisara al guardar. Al compartir la mutación, `isPending`
   * deshabilita TODOS los switches mientras un guardado está en curso, así
   * cada toggle siempre parte de la config ya actualizada por el anterior.
   */
  const toggleAgent = useMutation({
    mutationFn: ({ agentId, activo }: { agentId: string; activo: boolean }) => {
      const cfg = qc.getQueryData<IaConfig>(["ia", "config"]) ?? config!;
      const agentesActivos = activo
        ? [...new Set([...cfg.agentesActivos, agentId])]
        : cfg.agentesActivos.filter((id) => id !== agentId);
      return iaService.saveConfig({
        proveedor: cfg.proveedor,
        modelo: cfg.modelo,
        idioma: cfg.idioma,
        nivelDetalle: cfg.nivelDetalle,
        foco: cfg.foco,
        agentesActivos,
      });
    },
    onSuccess: (data) => {
      qc.setQueryData(["ia", "config"], data);
      qc.invalidateQueries({ queryKey: ["ia", "agents"] });
    },
    onError: (e: Error) => toast.error(e.message || "No se pudo actualizar el agente"),
  });

  return (
    <AppShell
      title="Motor IA"
      subtitle="Configura tu proveedor, tus reglas y los agentes que analizan cada licitación"
    >
      <div className="relative mb-6 overflow-hidden rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-6">
        <div className="absolute inset-0 grid-pattern opacity-[0.04]" />
        <div className="relative grid gap-4 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <div className="mb-3 inline-flex items-center gap-2 rounded bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
              <Sparkles className="size-3.5" />
              {config ? `${config.proveedor} · ${config.modelo}` : "Cargando motor…"}
            </div>
            <h2 className="mb-2 text-balance font-display text-2xl font-semibold">
              Tu motor de IA, con tus claves y tus reglas
            </h2>
            <p className="text-sm text-muted-foreground">
              Cada empresa conecta su propio proveedor (OpenAI, Anthropic o Gemini) y define las
              reglas de negocio que la IA debe respetar al analizar licitaciones de Mercado Público.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:col-span-2 lg:grid-cols-2">
            <Stat label="Estado del motor" value={badge.label} cls={badge.cls} />
            <Stat
              label="Agentes activos"
              value={`${agentsQuery.data?.filter((a) => a.activo).length ?? 0}/${agentsQuery.data?.length ?? 0}`}
            />
            <Stat label="Ejecuciones" value={String(metricsQuery.data?.ejecuciones ?? 0)} />
            <Stat label="Tasa de éxito" value={`${metricsQuery.data?.exito ?? 0}%`} />
          </div>
        </div>
      </div>

      <Tabs
        value={tab ?? "agentes"}
        onValueChange={(v) => navigate({ search: { tab: v as Tab } })}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="agentes">
            <Bot className="size-4" /> Agentes
          </TabsTrigger>
          <TabsTrigger value="config">
            <Settings2 className="size-4" /> Configuración
          </TabsTrigger>
          <TabsTrigger value="reglas">
            <Scale className="size-4" /> Reglas
          </TabsTrigger>
          <TabsTrigger value="actividad">
            <Activity className="size-4" /> Actividad
          </TabsTrigger>
        </TabsList>

        <TabsContent value="agentes">
          <div className="grid gap-4 xl:grid-cols-3">
            <div className="xl:col-span-2">
              {agentsQuery.isLoading || !config ? (
                <div className="grid gap-3 md:grid-cols-2">
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-48 w-full" />
                  ))}
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {agentsQuery.data?.map((a) => (
                    <AgentCard
                      key={a.id}
                      agent={a}
                      pending={toggleAgent.isPending}
                      onToggle={(activo) => toggleAgent.mutate({ agentId: a.id, activo })}
                    />
                  ))}
                </div>
              )}
            </div>
            {config && <IaChat config={config} />}
          </div>
        </TabsContent>

        <TabsContent value="config">
          {configQuery.isLoading || !config ? (
            <Skeleton className="h-96 max-w-2xl" />
          ) : (
            <EngineConfigForm config={config} />
          )}
        </TabsContent>

        <TabsContent value="reglas">
          <ReglasPanel />
        </TabsContent>

        <TabsContent value="actividad">
          {ejecucionesQuery.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : !ejecucionesQuery.data?.length ? (
            <div className="rounded-xl border border-dashed border-border p-10 text-center">
              <Activity className="mx-auto mb-3 size-8 text-muted-foreground" />
              <p className="text-sm font-medium">Sin ejecuciones todavía</p>
              <p className="text-xs text-muted-foreground">
                Cuando uses un agente o el asistente, verás aquí el historial y su latencia.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {ejecucionesQuery.data.map((e) => (
                <li key={e.id} className="rounded-lg border border-border bg-card p-3">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-medium">{e.agenteId}</span>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] ${
                        e.estado === "ok"
                          ? "bg-success/10 text-success"
                          : "bg-destructive/10 text-destructive"
                      }`}
                    >
                      {e.estado === "ok" ? "OK" : "Error"}
                    </span>
                    <span className="text-muted-foreground">
                      {new Date(e.createdAt).toLocaleString("es-CL")}
                    </span>
                    {e.latenciaMs != null && (
                      <span className="text-muted-foreground">{e.latenciaMs} ms</span>
                    )}
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                    {e.error ?? e.respuesta ?? e.prompt}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function Stat({ label, value, cls }: { label: string; value: string; cls?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card/60 p-3 backdrop-blur">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={`mt-0.5 inline-block rounded px-1.5 py-0.5 font-display text-sm font-semibold ${cls ?? "text-foreground"}`}
      >
        {value}
      </div>
    </div>
  );
}
